import { Outlet } from "@remix-run/react";
import { useState } from "react";

import Sidebar from "#app/components/layout/side-bar";
import { UserUIContext } from "#app/contexts/user-ui";

/**
 * The app shell for everything under `/settings`.
 *
 * This did not exist: each settings page rendered its own `<Sidebar />` and
 * then tried to offset the content around it by hand. Both attempts predated
 * the new sidebar and were wrong in the same way — `billing.tsx` used
 * `sm:ml-[65px]` and `organizations+/_layout.tsx` used `sm:pl-16`, i.e. the
 * width of the OLD 64px collapsed rail. The current sidebar is 280px when
 * expanded, so ~215px of every settings page was rendering underneath it.
 *
 * Structurally identical to `u+/_layout.tsx`, and for the same reasons: the
 * sidebar is a flex SIBLING that sizes itself, and the content column is the
 * scrollport rather than the document. That last part is what lets
 * `PageHeader`'s `sticky top-0` work — an ancestor with a non-`visible`
 * overflow becomes the sticky element's scrollport, so a page that scrolls the
 * document instead has nothing to stick to.
 */
export default function SettingsLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [fullScreenLayout, setFullScreenLayout] = useState(false);

  return (
    <UserUIContext.Provider
      value={{
        fullScreenLayout,
        setFullScreenLayout,
        sidebarOpen,
        setSidebarOpen,
      }}
    >
      {/* `fixed inset-0`, not just `h-dvh`.
       *
       * `h-dvh` makes the shell exactly one viewport tall, but it does not stop
       * the DOCUMENT from scrolling if anything else in the body adds height --
       * and when the document scrolls, the sidebar rides up with it. It is
       * `sticky top-0 h-dvh` (`side-bar.tsx:507`) inside a parent that is
       * exactly as tall as it is, so it has no slack to stick within: one pixel
       * of document scroll moves it the full pixel. That is the "whole view
       * goes up along with the sidebar" report.
       *
       * Taking the shell out of flow removes the question entirely -- there is
       * nothing left for the document to scroll. This is the same treatment
       * `root.tsx` already applies to the docs routes, for the same reason, and
       * it preserves what the note below depends on: the content column, not
       * the document, is the scrollport. */}
      <div
        className="fixed inset-0 flex w-full overflow-hidden overscroll-none bg-[#FAFCFD] dark:bg-[#0E0E0E]"
        style={{ height: "100dvh", maxHeight: "100vh" }}
      >
        {fullScreenLayout ? null : <Sidebar />}

        {/* `min-h-0` is what allows a flex child to be shorter than its content
            and therefore to scroll at all. */}
        <div className="layout-right-block flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
          <Outlet />
        </div>
      </div>
    </UserUIContext.Provider>
  );
}
